import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AddCircleOutline,
  Group,
  VerifiedUser,
  PersonRemove,
  PersonAdd,
} from "@mui/icons-material";
import {
  Button,
  IconButton,
  Switch,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import AddEmployeeModal from "./AddEmployeeForm";
import "../../styles/styles.css";
import "../../styles/layout.css";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ExportButton from "../../components/ag grid/ExportButton";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import EmployeeProfileView from "./ViewEmployee";
import EmployeeProfileVeiwController from "../../redux/actions/EmployeeProfileApiAction";
import usePermission from "../../utils/usePermission";
import { getUserInfoFromToken } from "../../utils/authUtils";
import FilterEmployeeDrawer from "./FilterEmployee";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx, BRAND } from "../../components/auth/authTheme";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import EmployeeMobileView from "./EmployeeMobileView";
import EmployeeMobileDetailView from "./EmployeeMobileDetailView";

const EMPTY_EMPLOYEES = [];

const applySearchFilter = (rows, searchTerm) => {
  if (!searchTerm.trim()) return rows;

  const q = searchTerm.toLowerCase();
  return rows.filter(
    (emp) =>
      emp.first_name?.toLowerCase().includes(q) ||
      emp.last_name?.toLowerCase().includes(q) ||
      emp.email?.toLowerCase().includes(q) ||
      emp.mobile_number?.toLowerCase().includes(q) ||
      emp.employee_id?.toLowerCase().includes(q)
  );
};

const UserListPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('filter') || 'all';

  const handleStatCardClick = useCallback((filter) => {
    setSearchParams({ filter });
  }, [setSearchParams]);

  const dispatch = useDispatch();
  const gridRef = useRef(null);

  const [editEmployee, setEditEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [open, setOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importPassword, setImportPassword] = useState("");
  const [showImportPassword, setShowImportPassword] = useState(false);
  const [importFile, setImportFile] = useState(null);

  const { getEmployees, deleteEmployee, updateEmployee, addBulkEmployeeData } =
    EmployeeProfileVeiwController();
  const updateEmployeeRef = useRef(updateEmployee);
  const deleteEmployeeRef = useRef(deleteEmployee);
  const dispatchRef = useRef(dispatch);
  updateEmployeeRef.current = updateEmployee;
  deleteEmployeeRef.current = deleteEmployee;
  dispatchRef.current = dispatch;

  const { employee_data } = useSelector((state) => state.employeeProfile);
  const employees = employee_data?.employees ?? EMPTY_EMPLOYEES;
  const [userType, setUserType] = useState(null);
  const { hasPermission } = usePermission();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState(null);

  useEffect(() => {
    dispatch(getEmployees());
  }, [dispatch]);

  const employeeListKey = useMemo(
    () =>
      employees.length
        ? employees.map((employee) => employee.id).join("|")
        : "empty",
    [employees]
  );

  useEffect(() => {
    setFilteredEmployees(null);
  }, [employeeListKey]);

  useEffect(() => {
    const { userType: type } = getUserInfoFromToken();
    setUserType(type);
  }, []);

  const handleDelete = useCallback(
    (employee) => {
      dispatch(deleteEmployeeRef.current(employee.id));
    },
    [dispatch]
  );

  const handleSelectionChanged = useCallback((rows) => {
    setSelectedRows(rows);
  }, []);

  const handleEdit = useCallback((employee) => {
    setEditEmployee(employee);
    setOpen(true);
  }, []);

  const handleView = useCallback((employee) => {
    setSelectedEmployee(employee);
    setShowViewModal(true);
  }, []);

  const openModalForCreate = () => {
    setEditEmployee(null);
    setOpen(true);
  };

  const handleToggleActive = useCallback((employee, checked) => {
    const updatedData = {
      is_active: checked,
      email: employee.email,
      mobile_number: employee.mobile_number,
    };
    dispatchRef.current(updateEmployeeRef.current(employee.id, updatedData));
  }, []);

  const baseEmployees = filteredEmployees ?? employees;

  const finalEmployees = useMemo(() => {
    let list = baseEmployees;
    if (statusFilter === 'active') {
      list = list.filter(e => e.is_active);
    } else if (statusFilter === 'inactive') {
      list = list.filter(e => !e.is_active);
    } else if (statusFilter === 'new') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      list = list.filter(e => {
        if (!e.profile_data?.joining_date) return false;
        const joined = new Date(e.profile_data.joining_date);
        return joined >= thirtyDaysAgo;
      });
    }
    return applySearchFilter(list, searchTerm);
  }, [baseEmployees, searchTerm, statusFilter]);

  const handleFilterApply = useCallback((filtered) => {
    setFilteredEmployees(filtered);
  }, []);

  const canCreate =
    userType === "Admin" || hasPermission("employee_list", "create");
  const canImport =
    userType === "Admin" || hasPermission("employee_list", "import");
  const canExport =
    userType === "Admin" || hasPermission("employee_list", "export");
  const canEdit =
    userType === "Admin" || hasPermission("employee_list", "edit");
  const canDelete =
    userType === "Admin" || hasPermission("employee_list", "delete");

  const canEditRef = useRef(canEdit);
  const canDeleteRef = useRef(canDelete);
  canEditRef.current = canEdit;
  canDeleteRef.current = canDelete;

  useEffect(() => {
    gridRef.current?.refreshCells?.({ force: true, columns: ["actions"] });
  }, [canEdit, canDelete]);

  const columnDefs = useMemo(
    () =>
      [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "Emp ID",
        field: "employee_id",
        width: 90,
        minWidth: 80,
        maxWidth: 110,
        pinned: "left",
        suppressSizeToFit: true,
      },
      {
        headerName: "Name",
        valueGetter: (params) =>
          `${params.data.first_name || ""} ${params.data.last_name || ""}`.trim(),
        flex: 1.5,
        minWidth: 160,
      },
      {
        headerName: "Email",
        field: "email",
        tooltipValueGetter: (params) => params.data ? params.data.email || "—" : "",
        flex: 1.5,
        minWidth: 180,
        cellStyle: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
      },
      {
        headerName: "Phone",
        field: "mobile_number",
        width: 130,
        minWidth: 120,
        maxWidth: 150,
        suppressSizeToFit: true,
      },
      {
        headerName: "Department",
        field: "profile_data.department_name",
        tooltipValueGetter: (params) => params.data ? params.data.profile_data?.department_name || "—" : "",
        flex: 1,
        minWidth: 130,
        cellStyle: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
      },
      userType === "Admin"
        ? {
          headerName: "Company Name",
          field: "company.company_name",
          tooltipValueGetter: (params) => params.data ? params.data.company?.company_name || "—" : "",
          flex: 1,
          minWidth: 140,
          cellStyle: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
        }
        : userType === "Employer"
          ? null
          : {
          headerName: "Designation",
          field: "profile_data.designation_name",
          tooltipValueGetter: (params) => params.data ? params.data.profile_data?.designation_name || "—" : "",
          flex: 1,
          minWidth: 130,
          cellStyle: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
        },
      {
        headerName: "Joining Date",
        field: "profile_data.joining_date",
        width: 120,
        minWidth: 110,
        maxWidth: 140,
        suppressSizeToFit: true,
      },
      {
        headerName: "Face",
        field: "face_registered",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        suppressSizeToFit: true,
        sortable: true,
        cellRenderer: (params) => {
          const registered = params.value;
          return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 8px",
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: registered ? "#e8f5e9" : "#f5f5f5",
                  color: registered ? "#2e7d32" : "#757575",
                  border: `1px solid ${registered ? "#a5d6a7" : "#e0e0e0"}`,
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ fontSize: 13 }}>{registered ? "✅" : "❌"}</span>
                {registered ? "Done" : "Pending"}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Active",
        field: "is_active",
        width: 90,
        minWidth: 90,
        maxWidth: 100,
        suppressSizeToFit: true,
        cellRenderer: (params) => {
          const handleChange = (e) => {
            const updatedValue = e.target.checked;
            params.node.setDataValue("is_active", updatedValue);

            const updatedData = {
              is_active: updatedValue,
              email: params.data.email,
              mobile_number: params.data.mobile_number,
            };

            dispatchRef.current(
              updateEmployeeRef.current(params.data.id, updatedData)
            ).catch(() => {
              params.node.setDataValue("is_active", !updatedValue);
            });
          };

          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Switch
                checked={Boolean(params.value)}
                onChange={handleChange}
                color="primary"
                size="small"
              />
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        field: "actions",
        pinned: "right",
        width: 130,
        minWidth: 130,
        maxWidth: 130,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Tooltip title="View">
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleView(params.data)}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            {canEditRef.current && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleEdit(params.data)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDeleteRef.current && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  sx={{ color: "#d32f2f" }}
                  onClick={() => handleDelete(params.data)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ),
      },
    ].filter(Boolean),
    [userType, handleView, handleEdit, handleDelete]
  );

  const emptyHint = useMemo(
    () =>
      canCreate
        ? 'Click "Add Employee" to onboard your first team member.'
        : "No employee records match your search or filters.",
    [canCreate]
  );

  const getRowId = useCallback((params) => String(params.data.id), []);

  return (
    <>
      {isMobile ? (
        <EmployeeMobileView
          employees={finalEmployees}
          employee_data={employee_data}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleView={handleView}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          openModalForCreate={openModalForCreate}
          canCreate={canCreate}
          canEdit={canEditRef.current}
          canDelete={canDeleteRef.current}
          userType={userType}
          handleToggleActive={handleToggleActive}
          handleStatCardClick={handleStatCardClick}
          statusFilter={statusFilter}
        />
      ) : (
        <ListPageLayout
          title="Employees"
          subtitle="Manage employee records, attendance, and profiles"
          headerAction={
            canCreate && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutline />}
                onClick={openModalForCreate}
                sx={toolbarButtonSx}
              >
                Add Employee
              </Button>
            )
          }
          toolbarLeft={
            canImport && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<UploadFileIcon />}
                onClick={() => setIsImportModalOpen(true)}
                sx={toolbarButtonSx}
              >
                Import
              </Button>
            )
          }
          toolbarRight={
            <>
              {canExport && (
                <ExportButton
                  gridRef={gridRef}
                  fileName="EmployeeList.xlsx"
                  exportColumns={[
                    "employee_id",
                    "first_name",
                    "last_name",
                    "email",
                    "mobile_number",
                    "department",
                    "designation",
                    "joining_date",
                    "is_active",
                  ]}
                  disabled={selectedRows.length === 0}
                  onSelectionCleared={() => setSelectedRows([])}
                />
              )}
              <TextField
                size="small"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ minWidth: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title="Filter">
                <IconButton
                  size="small"
                  onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "10px",
                    width: 40,
                    height: 40,
                  }}
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <PageSizeSelector
                pageSize={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              />
            </>
          }
          stats={
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <div 
                  className="stat-card stat-card-compact"
                  onClick={() => handleStatCardClick('all')}
                  style={{ cursor: 'pointer', border: statusFilter === 'all' ? `1px solid ${BRAND.orange}` : '1px solid transparent' }}
                >
                  <div className="avatar rounded-circle bg-primary">
                    <Group className="white-icon" />
                  </div>
                  <div className="stat-card-compact-body">
                    <h6>Total Employee</h6>
                    <h3 className="value">{employee_data?.total_employees || 0}</h3>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <div 
                  className="stat-card stat-card-compact"
                  onClick={() => handleStatCardClick('active')}
                  style={{ cursor: 'pointer', border: statusFilter === 'active' ? `1px solid ${BRAND.orange}` : '1px solid transparent' }}
                >
                  <div className="avatar rounded-circle bg-info">
                    <VerifiedUser className="white-icon" />
                  </div>
                  <div className="stat-card-compact-body">
                    <h6>Active</h6>
                    <h3 className="value">{employee_data?.active_employees || 0}</h3>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <div 
                  className="stat-card stat-card-compact"
                  onClick={() => handleStatCardClick('inactive')}
                  style={{ cursor: 'pointer', border: statusFilter === 'inactive' ? `1px solid ${BRAND.orange}` : '1px solid transparent' }}
                >
                  <div className="avatar rounded-circle bg-primary">
                    <PersonRemove className="white-icon" />
                  </div>
                  <div className="stat-card-compact-body">
                    <h6>InActive</h6>
                    <h3 className="value">
                      {employee_data?.inactive_employees || 0}
                    </h3>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <div 
                  className="stat-card stat-card-compact"
                  onClick={() => handleStatCardClick('new')}
                  style={{ cursor: 'pointer', border: statusFilter === 'new' ? `1px solid ${BRAND.orange}` : '1px solid transparent' }}
                >
                  <div className="avatar rounded-circle bg-danger">
                    <PersonAdd className="white-icon" />
                  </div>
                  <div className="stat-card-compact-body">
                    <h6>New Joiners</h6>
                    <h3 className="value">{employee_data?.new_joiners_count || 0}</h3>
                  </div>
                </div>
              </Grid>
            </Grid>
          }
        >
          <div className="list-page-grid">
            <ReusableGrid
              rowData={finalEmployees}
              columnDefs={columnDefs}
              gridRef={gridRef}
              pagination={true}
              pageSize={pageSize}
              onSelectionChanged={handleSelectionChanged}
              getRowId={getRowId}
              emptyMessage="No employees yet"
              emptyHint={emptyHint}
            />
          </div>
        </ListPageLayout>
      )}

      {open && (
        <AddEmployeeModal
          open
          handleClose={() => {
            setOpen(false);
            setEditEmployee(null);
          }}
          initialData={editEmployee}
        />
      )}
      {isMobile ? (
        <EmployeeMobileDetailView
          show={showViewModal}
          handleClose={() => setShowViewModal(false)}
          selectedEmployee={selectedEmployee}
        />
      ) : (
        <EmployeeProfileView
          show={showViewModal}
          handleClose={() => setShowViewModal(false)}
          selectedEmployee={selectedEmployee}
        />
      )}
      <FilterEmployeeDrawer
        anchorEl={filterAnchorEl}
        handleClose={() => setFilterAnchorEl(null)}
        employees={employees}
        onApply={handleFilterApply}
      />
      
      <Dialog
        open={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          setImportPassword("");
          setShowImportPassword(false);
          setImportFile(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Employees</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Set a default password for all imported employees and select an Excel file.
          </Typography>
          <TextField
            fullWidth
            label="Password"
            type={showImportPassword ? "text" : "password"}
            value={importPassword}
            onChange={(e) => setImportPassword(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowImportPassword(!showImportPassword)}
                    edge="end"
                  >
                    {showImportPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            fullWidth
          >
            {importFile ? importFile.name : "Select Excel File"}
            <input
              type="file"
              hidden
              accept=".xlsx,.xls"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) setImportFile(file);
                e.target.value = "";
              }}
            />
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => {
              setIsImportModalOpen(false);
              setImportPassword("");
              setShowImportPassword(false);
              setImportFile(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!importFile}
            onClick={() => {
              dispatch(addBulkEmployeeData({ file: importFile, password: importPassword }));
              setIsImportModalOpen(false);
              setImportPassword("");
              setShowImportPassword(false);
              setImportFile(null);
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserListPage;
